import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Wrench, FileDown, CheckCircle, ArrowRight, Calculator, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { useSettings } from '../contexts/SettingsContext';
import { getOffer } from '../lyphline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RATE_SHEET_PATH = '/lyphline-rate-sheet-case-study.html';

// Newsletter Popup Component with Exit Intent Detection
// Settings are loaded from backend (admin configurable)
export const NewsletterPopup = () => {
  const { settings: siteSettings } = useSettings();
  const offer = getOffer(siteSettings);
  const [isOpen, setIsOpen] = useState(false);
  const [variant, setVariant] = useState(null);
  const variantRef = useRef(null);

  // Assign a sticky A/B/C variant per visitor.
  useEffect(() => {
    const variants = ['A', 'B', 'C'];
    let v = localStorage.getItem('lyphline_popup_variant');
    if (!variants.includes(v)) {
      v = variants[Math.floor(Math.random() * variants.length)];
      localStorage.setItem('lyphline_popup_variant', v);
    }
    variantRef.current = v;
    setVariant(v);
  }, []);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [triggered, setTriggered] = useState(false);
  const [triggerType, setTriggerType] = useState('timed'); // 'timed' or 'exit_intent'
  
  // Dynamic settings from backend
  const [settings, setSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch popup settings from backend (with cache-busting)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Add timestamp to bust browser cache
        const timestamp = Date.now();
        const res = await axios.get(`${API_URL}/api/marketing/popup-settings/public?_t=${timestamp}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        setSettings(res.data);
        setSettingsLoaded(true);
      } catch (error) {
        console.error('Failed to fetch popup settings:', error);
        // Use defaults if fetch fails - longer delay for better UX
        setSettings({
          timed_popup: {
            enabled: true,
            delay_seconds: 30,
            headline: "Get 10% Off Your First Order!",
            body: "Subscribe to our newsletter and receive exclusive deals.",
            cta_text: "Subscribe Now",
            offer_code: "WELCOME10"
          },
          exit_intent_popup: {
            enabled: true,
            headline: "Wait! Don't Leave Empty-Handed",
            body: "Sign up for our newsletter and get exclusive discounts on your first order.",
            cta_text: "Get My Discount",
            offer_code: "SAVE10"
          }
        });
        setSettingsLoaded(true);
      }
    };
    
    fetchSettings();
  }, []);

  // Get current popup config based on trigger type
  const getCurrentConfig = useCallback(() => {
    if (!settings) return null;
    return triggerType === 'exit_intent' ? settings.exit_intent_popup : settings.timed_popup;
  }, [settings, triggerType]);

  // Track popup impression
  const trackImpression = useCallback(async (type) => {
    try {
      await axios.post(`${API_URL}/api/popup/track?event_type=impression&trigger_type=${type}&page=site&variant=${variantRef.current || ''}`);
    } catch (e) {
      // Silent fail
    }
  }, []);

  // Track popup conversion
  const trackConversion = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/popup/track?event_type=conversion&trigger_type=${triggerType}&page=site&variant=${variantRef.current || ''}`);
    } catch (e) {
      // Silent fail
    }
  }, [triggerType]);

  const goDiagnostic = () => {
    trackConversion();
    localStorage.setItem('newsletter_subscribed', 'true');
    setIsOpen(false);
    window.location.href = '/repair/quote';
  };

  const goCalculator = () => {
    trackConversion();
    localStorage.setItem('newsletter_subscribed', 'true');
    setIsOpen(false);
    window.location.href = '/#downtime';
  };

  // Exit intent detection
  const handleMouseLeave = useCallback((e) => {
    if (!settings?.exit_intent_popup?.enabled) return;
    
    if (e.clientY <= 0 && !triggered) {
      const dismissed = localStorage.getItem('newsletter_dismissed');
      const alreadySubscribed = localStorage.getItem('newsletter_subscribed');
      
      if (!dismissed && !alreadySubscribed) {
        setTriggerType('exit_intent');
        setIsOpen(true);
        setTriggered(true);
        trackImpression('exit_intent');
      }
    }
  }, [triggered, trackImpression, settings]);

  // Setup popup triggers based on settings
  useEffect(() => {
    if (!settingsLoaded || !settings) return;
    
    // Don't show popup on admin pages, cart/checkout, or product browsing pages
    if (window.location.pathname.startsWith('/admin') || 
        window.location.pathname.startsWith('/cart') ||
        window.location.pathname.startsWith('/checkout') ||
        window.location.pathname.startsWith('/products') ||
        window.location.pathname.startsWith('/entrepreneur') ||
        window.location.pathname.startsWith('/commercial')) {
      return;
    }
    
    const dismissed = localStorage.getItem('newsletter_dismissed');
    const alreadySubscribed = localStorage.getItem('newsletter_subscribed');
    const sessionShown = sessionStorage.getItem('newsletter_shown_this_session');
    
    // Don't show if already dismissed, subscribed, or shown this session
    if (dismissed || alreadySubscribed || sessionShown) {
      return;
    }

    // Exit intent (only on desktop)
    if (settings.exit_intent_popup?.enabled) {
      document.addEventListener('mouseleave', handleMouseLeave);
    }

    // Don't interrupt a user who is actively filling out a form
    const isUserFillingForm = () => {
      const ae = document.activeElement;
      if (ae && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName)) return true;
      const fields = document.querySelectorAll('input, textarea');
      for (const f of fields) {
        const t = (f.getAttribute('type') || 'text').toLowerCase();
        if (t === 'hidden' || t === 'checkbox' || t === 'radio') continue;
        if (f.value && f.value.trim().length > 0) return true;
      }
      return false;
    };

    // Timed popup - use longer delay (30 seconds default instead of 5)
    let timer;
    if (settings.timed_popup?.enabled) {
      const delayMs = Math.max((settings.timed_popup.delay_seconds || 30) * 1000, 15000); // Minimum 15 seconds
      const attemptTimed = () => {
        if (triggered) return;
        if (isUserFillingForm()) {
          timer = setTimeout(attemptTimed, 8000); // retry once they're done
          return;
        }
        setTriggerType('timed');
        setIsOpen(true);
        setTriggered(true);
        sessionStorage.setItem('newsletter_shown_this_session', 'true');
        trackImpression('timed');
      };
      timer = setTimeout(attemptTimed, delayMs);
    }

    // Mobile scroll trigger (70% scroll instead of 50%)
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!triggered && window.innerWidth < 768 && settings.timed_popup?.enabled && !isUserFillingForm()) {
          const scrollPercentage = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
          if (scrollPercentage > 70) {
            setTriggerType('timed');
            setIsOpen(true);
            setTriggered(true);
            sessionStorage.setItem('newsletter_shown_this_session', 'true');
            trackImpression('timed');
          }
        }
      }, 500);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (timer) clearTimeout(timer);
      clearTimeout(scrollTimeout);
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [settingsLoaded, settings, handleMouseLeave, triggered, trackImpression]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('newsletter_dismissed', 'true');
    sessionStorage.setItem('newsletter_shown_this_session', 'true');
  };

  // Handle clicking outside the popup to close it
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle Escape key to close popup
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/case-study`, {
        email,
        download_url: `${window.location.origin.replace(/\/$/, '')}${RATE_SHEET_PATH}`,
        estimate_text: offer.active ? `Popup signup, offer ${offer.code}` : 'Popup signup',
      });
      setSubscribed(true);
      localStorage.setItem('newsletter_subscribed', 'true');
      trackConversion();
    } catch (error) {
      console.error('Popup submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get current popup configuration (used only to gate enabled/trigger logic)
  const config = getCurrentConfig();

  if (!isOpen || !config) return null;

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      data-testid="newsletter-popup"
      onClick={handleBackdropClick}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slide-up">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/80 hover:text-white z-10 p-1 hover:bg-white/10 rounded-full transition-colors"
          data-testid="newsletter-popup-close"
        >
          <X className="h-5 w-5" />
        </button>

        {subscribed ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-[#CE8946]/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-9 w-9 text-[#CE8946]" />
            </div>
            <h3 className="text-2xl font-bold text-[#003456] mb-2">Check your inbox!</h3>
            <p className="text-slate-600 mb-4 text-sm">We've sent your rate sheet &amp; downtime case study. You can open it right now too.</p>
            <a
              href={RATE_SHEET_PATH}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#003456] hover:bg-[#023F63] text-white font-bold px-6 py-3 rounded-md transition-colors"
              data-testid="newsletter-popup-open-sheet"
            >
              <FileDown className="h-4 w-4" /> Open the rate sheet
            </a>
          </div>
        ) : variant === 'B' ? (
          <>
            {/* Variant B: diagnostic-first */}
            <div className="p-6 text-white text-center bg-[#003456]">
              <div className="w-14 h-14 bg-[#CE8946]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Wrench className="h-7 w-7 text-[#CE8946]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Machine down right now?</h3>
              <p className="text-white/75 text-sm leading-relaxed">
                Get a fast, free diagnostic. Tell us what's happening and we'll send a quote to get you back online.
                {offer.active ? ` ${offer.headline} applies.` : ''}
              </p>
            </div>
            <div className="p-6">
              <button
                onClick={goDiagnostic}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] py-3 rounded-md font-bold transition-colors"
                data-testid="newsletter-popup-diagnostic-cta"
              >
                Start my free diagnostic <ArrowRight className="h-4 w-4" />
              </button>
              <div className="flex items-center justify-center gap-4 mt-4 text-sm">
                <a href="/documents" className="text-[#003456] font-semibold hover:text-[#CE8946] transition-colors" data-testid="newsletter-popup-downloads-b">Browse downloads</a>
              </div>
              <p className="text-xs text-slate-400 mt-4 text-center">No obligation, takes under a minute.</p>
            </div>
          </>
        ) : variant === 'C' ? (
          <>
            {/* Variant C: downtime-calculator teaser */}
            <div className="p-6 text-white text-center bg-[#003456]">
              <div className="w-14 h-14 bg-[#CE8946]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Calculator className="h-7 w-7 text-[#CE8946]" />
              </div>
              <h3 className="text-xl font-bold mb-2">What is downtime costing you?</h3>
              <p className="text-white/75 text-sm leading-relaxed">
                Every stalled cycle is lost product and lost revenue. Run the numbers in 30 seconds with our downtime calculator.
              </p>
            </div>
            <div className="p-6">
              <button
                onClick={goCalculator}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] py-3 rounded-md font-bold transition-colors"
                data-testid="newsletter-popup-calculator-cta"
              >
                Calculate my downtime cost <ArrowRight className="h-4 w-4" />
              </button>
              <div className="flex items-center justify-center gap-4 mt-4 text-sm">
                <a href="/repair/quote" className="text-[#003456] font-semibold hover:text-[#CE8946] transition-colors inline-flex items-center gap-1" data-testid="newsletter-popup-diagnostic-c"><Wrench className="h-3.5 w-3.5" /> Free diagnostic</a>
              </div>
              <p className="text-xs text-slate-400 mt-4 text-center">No obligation, see your numbers instantly.</p>
            </div>
          </>
        ) : (
          <>
            {/* Header */}
            <div className="p-6 text-white text-center bg-[#003456]">
              <div className="w-14 h-14 bg-[#CE8946]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <FileDown className="h-7 w-7 text-[#CE8946]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Free rate sheet &amp; downtime case study</h3>
              <p className="text-white/75 text-sm leading-relaxed">
                See what a stalled machine really costs, and how a rebuild pays for itself.
                {offer.active ? ` Plus ${offer.headline} on your rebuild.` : ''}
              </p>
            </div>

            {/* Offer code */}
            {offer.active && (
              <div className="px-6 -mt-4">
                <div className="bg-[#CE8946] rounded-xl p-3 text-center shadow-lg">
                  <p className="text-[11px] font-semibold text-[#003456]/70 mb-0.5 uppercase tracking-wide">Mention this offer code</p>
                  <p className="text-xl font-bold font-mono text-[#003456]">{offer.code}</p>
                </div>
              </div>
            )}

            {/* Form */}
            <div className="p-6 pt-4">
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full"
                  data-testid="newsletter-popup-email"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#CE8946] hover:bg-[#B87333] text-[#003456] py-3 font-bold"
                  data-testid="newsletter-popup-submit"
                >
                  {loading ? 'Sending…' : (<>Email me the rate sheet <ArrowRight className="h-4 w-4 ml-1" /></>)}
                </Button>
              </form>

              <div className="flex items-center justify-center gap-4 mt-4 text-sm">
                <a href="/documents" className="text-[#003456] font-semibold hover:text-[#CE8946] transition-colors" data-testid="newsletter-popup-downloads">Browse downloads</a>
                <span className="text-slate-300">·</span>
                <a href="/repair/quote" className="text-[#003456] font-semibold hover:text-[#CE8946] transition-colors inline-flex items-center gap-1" data-testid="newsletter-popup-diagnostic"><Wrench className="h-3.5 w-3.5" /> Free diagnostic</a>
              </div>

              <p className="text-xs text-slate-400 mt-4 text-center">No spam, ever. Unsubscribe anytime.</p>
            </div>
          </>
        )}

        <div className="hidden" data-testid="popup-trigger-type" data-trigger={triggerType} />
      </div>
    </div>
  );
};

// Newsletter Footer Form Component
export const NewsletterFooter = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email });
      setSubscribed(true);
      setEmail('');
      localStorage.setItem('newsletter_subscribed', 'true');
    } catch (error) {
      console.error('Subscription error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className="flex items-center gap-2 text-[#CE8946]">
        <CheckCircle className="h-5 w-5" />
        <span>Thanks for subscribing!</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <div className="relative flex-1">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50"
          required
          data-testid="newsletter-footer-email"
        />
      </div>
      <Button 
        type="submit" 
        disabled={loading}
        className="bg-[#003456] hover:bg-[#002B47] text-white"
        data-testid="newsletter-footer-submit"
      >
        {loading ? '...' : 'Subscribe'}
      </Button>
    </form>
  );
};

export default NewsletterPopup;
